import React, { useEffect, useState } from "react";
import { critiquePlan, verifyUnlockToken } from "@/lib/api";
import {
  canCritique,
  incrementUsage,
  remainingFreeCritiques,
  getUnlockToken,
  DAILY_CAP,
} from "@/lib/aiUsage";
import { Brain, AlertTriangle, AlertCircle, Info, Loader2, Lock, Sparkles, Twitter } from "lucide-react";
import { Link } from "react-router-dom";
import { buildShareUrl } from "@/lib/shareUrl";
import { fmtCurrency } from "@/lib/format";
import { errorMessage } from "@/lib/errorMessage";
import SponsorFooter from "@/components/SponsorFooter";

const SeverityIcon = ({ severity }) => {
  if (severity === "high") return <AlertTriangle size={14} className="text-[#C26747]" />;
  if (severity === "medium") return <AlertCircle size={14} className="text-[#C9A227]" />;
  return <Info size={14} className="text-[#2B5339]" />;
};

const SeverityChip = ({ severity }) => {
  const styles = {
    high: "bg-[#FBE9E0] text-[#C26747]",
    medium: "bg-[#FAF1DD] text-[#8F7218]",
    low: "bg-[#E8F0EB] text-[#2B5339]",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase font-semibold rounded-full px-2 py-0.5 ${styles[severity] || styles.low}`}>
      <SeverityIcon severity={severity} />
      {severity}
    </span>
  );
};

export default function PlanCritique({ inputs, result }) {
  const [loading, setLoading] = useState(false);
  const [critique, setCritique] = useState(null);
  const [err, setErr] = useState(null);
  const [hasToken, setHasToken] = useState(false);
  const [remaining, setRemaining] = useState(DAILY_CAP);

  useEffect(() => {
    let active = true;
    (async () => {
      const tok = getUnlockToken();
      if (!tok) {
        if (active) {
          setHasToken(false);
          setRemaining(remainingFreeCritiques(false));
        }
        return;
      }
      try {
        const v = await verifyUnlockToken(tok);
        const valid = v?.valid && v?.package_id === "ai_unlock_30d";
        if (active) {
          setHasToken(valid);
          setRemaining(valid ? Infinity : remainingFreeCritiques(false));
        }
      } catch {
        if (active) {
          setHasToken(false);
          setRemaining(remainingFreeCritiques(false));
        }
      }
    })();
    // Listen for storage events to refresh after payment completes in another tab
    const onStorage = (e) => {
      if (e.key === "centennial_unlock_token_v1") {
        // refetch
        const tok = getUnlockToken();
        if (tok) verifyUnlockToken(tok).then((v) => {
          const valid = v?.valid && v?.package_id === "ai_unlock_30d";
          if (active) {
            setHasToken(valid);
            setRemaining(valid ? Infinity : remainingFreeCritiques(false));
          }
        }).catch(() => {});
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      active = false;
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const run = async () => {
    if (!result) {
      setErr("Run a projection first.");
      return;
    }
    if (!canCritique(hasToken)) {
      setErr(`Daily free limit reached (${DAILY_CAP}/day). Unlock unlimited for 30 days for $3.`);
      return;
    }
    setLoading(true);
    setErr(null);
    setCritique(null);
    try {
      const { critique: c } = await critiquePlan(inputs, result.summary);
      setCritique(c);
      if (!hasToken) {
        const u = incrementUsage();
        setRemaining(Math.max(0, DAILY_CAP - u.count));
      }
    } catch (e) {
      console.error(e);
      setErr(errorMessage(e, "AI critic failed. Try again in a moment."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="plan-critique" className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold inline-flex items-center gap-2">
            <Brain size={12} /> ai plan critic
          </div>
          <div className="font-display text-lg text-[#1C2B23] mt-1">
            A fee-only advisor's take on your plan
          </div>
          <p className="text-[12px] text-[#5C6B62] mt-1">
            Powered by Claude Sonnet 4.5 — reads your plan and flags the top risks specific to your numbers.
          </p>
        </div>
        <button
          data-testid="critique-run"
          type="button"
          onClick={run}
          disabled={loading || !result}
          className="bg-[#2B5339] text-white hover:bg-[#1E3B28] disabled:opacity-60 rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {loading ? "Analyzing…" : critique ? "Re-analyze" : "Critique my plan"}
        </button>
      </div>

      <div className="flex items-center justify-between text-[11px] text-[#5C6B62]">
        <span>
          {hasToken ? (
            <span className="inline-flex items-center gap-1 text-[#2B5339] font-medium">
              <Sparkles size={11} /> 30-day unlimited active
            </span>
          ) : (
            <span>
              {remaining} of {DAILY_CAP} free critiques left today
            </span>
          )}
        </span>
        {!hasToken && (
          <Link
            data-testid="critique-upgrade-link"
            to="/pricing"
            className="text-[#2B5339] hover:underline inline-flex items-center gap-1"
          >
            <Lock size={11} /> Unlock unlimited · $3
          </Link>
        )}
      </div>

      {err && (
        <div data-testid="critique-error" className="mt-4 text-sm text-[#C26747] bg-[#FBE9E0]/60 border border-[#F5D0BD] rounded-lg px-4 py-3">
          {err}
        </div>
      )}

      {critique && (
        <div data-testid="critique-result" className="mt-5 space-y-3">
          <div className="border border-[#E5E2DC] rounded-xl p-4 bg-[#F9F8F6]/60 flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold mb-1">
                overall assessment
              </div>
              <p className="text-sm text-[#1C2B23] leading-relaxed">{critique.overall}</p>
            </div>
            <button
              data-testid="tweet-plan"
              type="button"
              onClick={() => {
                const s = result.summary;
                const text = `My retirement plan: ${s.success_probability_pct.toFixed(0)}% chance of ${fmtCurrency(s.median_final_balance)} at age ${inputs.life_expectancy}. Built it free in 30 seconds 👇`;
                const url = buildShareUrl(inputs);
                const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
                window.open(tweetUrl, "_blank", "noopener");
              }}
              className="bg-[#1C2B23] text-white hover:bg-black rounded-full px-4 py-2 text-xs inline-flex items-center gap-2 shrink-0"
              title="Share with auto-rendered preview card"
            >
              <Twitter size={13} /> Tweet my plan
            </button>
          </div>
          <ul className="space-y-3">
            {(critique.flags || []).map((f, i) => (
              <li
                key={`${f.severity}-${f.title}-${i}`}
                data-testid={`critique-flag-${i}`}
                className="border border-[#E5E2DC] rounded-xl p-4 bg-white"
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div className="font-display text-base text-[#1C2B23]">{f.title}</div>
                  <SeverityChip severity={f.severity} />
                </div>
                <p className="text-sm text-[#5C6B62] leading-relaxed">{f.detail}</p>
                <div className="mt-2 text-sm text-[#2B5339] inline-flex items-start gap-2">
                  <span className="font-medium">Suggestion:</span>
                  <span className="text-[#1C2B23]">{f.suggestion}</span>
                </div>
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-[#5C6B62] italic">
            AI-generated — not personalized financial advice. Always consult a licensed advisor before major decisions.
          </p>
          <SponsorFooter context="critique" />
        </div>
      )}

      {!critique && !err && !loading && (
        <div className="mt-5 text-sm text-[#5C6B62] border-2 border-dashed border-[#E5E2DC] rounded-xl p-6 text-center">
          Click "Critique my plan" to get a structured review.
        </div>
      )}
    </div>
  );
}
